import React from "react";
import { motion } from "framer-motion";
import { FaUsers, FaTrophy, FaServer, FaGlobe } from "react-icons/fa";

const credibilityPoints = [
  {
    icon: <FaUsers size={32} color="#38bdf8" />,
    title: "100+ Happy Clients",
    desc: "Trusted by startups and enterprises for end-to-end cloud infrastructure management.",
  },
  {
    icon: <FaTrophy size={32} color="#6366f1" />,
    title: "Successful Journey",
    desc: "Proven experience delivering secure, scalable, high-performing cloud solutions.",
  },
  {
    icon: <FaServer size={32} color="#facc15" />,
    title: "We Take Care of Infra",
    desc: "From design and operations to optimization and lifecycle management.",
  },
  {
    icon: <FaGlobe size={32} color="#34d399" />,
    title: "Clients Focus on Business",
    desc: "We manage the cloud, so clients focus on growth and innovation.",
  },
];

const stats = [
  { label: "Projects Completed", value: "50+" },
  { label: "Years of Experience", value: "4+" },
  { label: "Uptime Guarantee", value: "99.9%" },
  { label: "Regions Served", value: "2+" },
];

const TrustedByClients = () => {
  return (
    <section
      id="portfolio"
      style={{
        background: "#020617",
        padding: "50px 20px",
        position: "relative",
        overflow: "hidden",
        fontFamily: "'Roboto', sans-serif",
        color: "#e5e7eb",
      }}
    >
      {/* Subtle Glow */}
      <div style={glow("#38bdf8", "12%", "12%")} />
      <div style={glow("#6366f1", "82%", "70%")} />

      <div style={{ maxWidth: "1200px", margin: "0 auto", position: "relative" }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: "center", marginBottom: "40px" }}
        >
          <h2
            style={{
              fontSize: "2.3rem",
              fontWeight: 800,
              color: "#38bdf8",
              marginBottom: "10px",
            }}
          >
            Trusted by Our Clients
          </h2>
          <p
            style={{
              fontSize: "1rem",
              color: "#94a3b8",
              maxWidth: "700px",
              margin: "0 auto",
              lineHeight: "1.55",
            }}
          >
            Our clients focus on business growth while we ensure their cloud infrastructure remains secure, scalable, and reliable.
          </p>
        </motion.div>

        {/* Credibility Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: "22px",
            marginBottom: "45px",
          }}
        >
          {credibilityPoints.map((point, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -6, scale: 1.02 }}
              style={cardStyle}
            >
              <div style={iconWrap}>{point.icon}</div>
              <h3
                style={{
                  fontSize: "1.25rem",
                  marginBottom: "8px",
                  fontWeight: 700,
                }}
              >
                {point.title}
              </h3>
              <p
                style={{
                  fontSize: "0.92rem",
                  color: "#cbd5e1",
                  lineHeight: "1.55",
                }}
              >
                {point.desc}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Stats */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "30px",
          }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              style={statCardStyle}
            >
              <h3
                style={{
                  fontSize: "1.8rem",
                  fontWeight: 800,
                  color: "#38bdf8",
                  marginBottom: "4px",
                }}
              >
                {stat.value}
              </h3>
              <p style={{ fontSize: "0.95rem", color: "#94a3b8" }}>
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Styles ---------- */

const cardStyle = {
  background: "rgba(15, 23, 42, 0.6)",
  backdropFilter: "blur(10px)",
  borderRadius: "16px",
  padding: "26px 22px",
  border: "1px solid rgba(148,163,184,0.14)",
  boxShadow: "0 16px 36px rgba(0,0,0,0.35)",
  textAlign: "center",
};

const iconWrap = {
  width: "56px",
  height: "56px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "14px",
  background: "rgba(255,255,255,0.05)",
  margin: "0 auto 14px",
};

const statCardStyle = {
  background: "rgba(15, 23, 42, 0.45)",
  backdropFilter: "blur(10px)",
  borderRadius: "14px",
  padding: "20px 28px",
  textAlign: "center",
  minWidth: "150px",
  border: "1px solid rgba(148,163,184,0.14)",
};

const glow = (color, left, top) => ({
  position: "absolute",
  width: "380px",
  height: "380px",
  background: color,
  filter: "blur(150px)",
  opacity: 0.18,
  left,
  top,
});

export default TrustedByClients;
